import getRandomLifestyleImage from 'utils/getLifestyleImage';
import { isNumber } from 'constants/regexExpressions';
import createPictureImageSources from 'utils/createPictureImageSources';

/**
 * Represents a Basic Summary Card component.
 */
export default class BasicCommunitySummaryCard {
  /**
   * Sets the information for the summary card.
   *
   * @param {Object} summaryCard - The summary card object.
   */
  setInformation(summaryCard) {
    this.summaryCard = summaryCard;
    this.id = 'summaryCard' + this.summaryCard.Id;
    this.CommunityProjectType = this.summaryCard.CommunityProjectType;
    this.errorImage = getRandomLifestyleImage();
    // TODO: NewHomeSource Rebrand
    // Change sources on backend.
    const { sources, src } = createPictureImageSources({
      url: this.summaryCard.Thumbnail ?? this.errorImage,
      breakpoints: {
        '2x': {
          width: 286,
          height: 208,
        },
        '3x': {
          width: 350,
          height: 312,
        },
      },
      irsParameters: {
        mode: 'crop',
        progressive: true,
      },
    });
    this.sources = sources;
    this.src = src;
  }

  /**
   * Emits a custom event when the CTA (Call to Action) is clicked.
   */
  emitCtaClickEvent() {
    const element = this.getElement();
    const target = element.dataset.clickTarget;

    const callToActionClickEvent = new CustomEvent('summaryCardCtaClick', {
      detail: {
        name: 'leadGenerator',
        type: this.summaryCard.Type,
        id: this.summaryCard.Id,
        url: this.summaryCard.Url,
        marker: this.summaryCard,
        variation: 'basic',
        callToAction: 'Learn More',
        target,
      },
    });

    document.dispatchEvent(callToActionClickEvent);
  }

  /**
   * Emits a custom event when a link is clicked on the summary card.
   */
  emitLinkClickEvent() {
    const linkClickEvent = new CustomEvent('summaryCardLinkClick', {
      detail: {
        summaryCard: this.summaryCard,
      },
    });

    document.dispatchEvent(linkClickEvent);
  }

  /**
   * Handles the error event for the image element in the summary card.
   * If the image fails to load, it sets the error image as the source and removes all source elements from the parent
   * @param {Event} event - The error event object.
   */
  handleOnErrorImage(event) {
    const picture = event.target.parentElement;
    event.target.src = this.errorImage;

    picture?.querySelectorAll('source').forEach((source) => source.remove());
    event.target.onerror = null;
  }

  /**
   * Binds events to the summary card.
   */
  bindEvents() {
    const element = this.getElement();

    const callToActionButton = element.querySelector('[data-summary-card-cta]');
    callToActionButton.addEventListener('click', this.emitCtaClickEvent.bind(this));

    const link = element.querySelector('[data-summary-card-link]');
    link.addEventListener('click', this.emitLinkClickEvent.bind(this));

    const image = element.querySelector('img');

    // remove all sources and set the lifestyle error image as the src for the image
    // if the image fails loading.
    image?.addEventListener('error', this.handleOnErrorImage.bind(this));
  }

  /**
   * Gets the HTML element with the specified ID.
   * @returns {HTMLElement} The HTML anchor element.
   */
  getElement() {
    return document.getElementById(this.id);
  }

  /**
   * Get the price of the summary card.
   * @returns {string} The price of the summary card.
   */
  getPrice() {
    const price = this.summaryCard.Price;
    const containsNumbers = isNumber.test(price);

    if (containsNumbers) {
      return `From ${price}`;
    }

    return price;
  }

  /**
   * Retrieves the image element for the summary card.
   *
   * @returns {string} The HTML string representing the image element.
   */
  getImage() {
    return `
            <picture class='nhs-c-card--summary__image'>
                ${this.sources
                  .map((source) => `<source srcset="${source.srcset}" type="${source.format}"></source>`)
                  .join('')}
                <img
                    src="${this.src}"
                    data-qa="map-image"
                    data-summary-img
                    class="nhs-c-card--summary__image"
                    data-src="${this.errorImage}"
                >
            </picture>
        `;
  }

  /**
   * Sets the label for the BasicSummaryCard.
   * @param {string} label - The label to set.
   */
  setLabel(label) {
    this.label = label;
  }

  /**
   * Returns the HTML representation of the summary card.
   * @returns {string} The HTML string representing the summary card.
   */
  getHtml() {
    return `
            <div id="summaryCard${
              this.summaryCard.Id
            }" data-qa="map-card" data-summary-content class="nhs-c-card--summary nhs-mq-card--summary-medium nhs-c-carousel__element"
                data-card
                data-id="${this.summaryCard.Id}"
                data-latitude="${this.summaryCard.Latitude}"
                data-longitude="${this.summaryCard.Longitude}"
                data-community-type="${this.summaryCard.CommunityProjectType.Key}"
                data-community-id="${this.summaryCard.Id}"
                data-market-id="${this.summaryCard.MarketId}"
                data-brand-name="${this.summaryCard.BrandName}"
                data-city="${this.summaryCard.City}"
                data-type="${this.summaryCard.Type}"
                data-zip="${this.summaryCard.Zip}"
                data-url="${this.summaryCard.Url}"
                data-has-builder-reviews="${this.summaryCard.BuilderRating > 0}"
                data-price-low="${this.summaryCard.PriceLow}"
                data-price-high="${this.summaryCard.PriceHigh}"
                data-community-name="${this.summaryCard.Name}"
                data-marketing-status="${this.summaryCard.MarketingStatus.Name}"
                data-price-reduction-label="${this.summaryCard.PriceReductionLabel}"
                data-has-limited-information="${this.summaryCard.IsLimited}"
                data-contact-vendor-name="${this.summaryCard.BasicContactVendorName}"
                data-click-target="SRP Basic Map Card"
                data-is-basic="true"
                data-zip-code="${this.summaryCard.Zip}"
                data-builder-id="${this.summaryCard.BuilderId}"
                data-market-name="${this.summaryCard.MarketName}"
                data-state-abbreviation="${this.summaryCard.StateAbbreviation}"
                data-state-name="${this.summaryCard.StateName}"
            >

                ${this.label.getHtml()}
                ${this.getImage()}
                <button 
                    class="nhs-c-button--primary-lead nhs-c-button--small-24 nhs-c-button--text-only nhs-c-card--summary__call-to-action" 
                    data-summary-card-cta
                    data-qa="map-cta"
                    data-trusted-form="true"
                    data-lead-generator
                    data-lead-generator-opener="get-information-basic"
                    data-button-action="Learn More"
                    data-from-page="${this.summaryCard.FromPage}">
                    Learn More
                </button>
                <a href="${
                  this.summaryCard.Url
                }" data-summary-card-link target="_blank" class="nhs-c-card--summary__body">
                    <h3 class="nhs-c-card--summary__price" data-qa="Map-Price"><span class='nhs-u-text--ellipsis'>${this.getPrice()}</span></h3>
                    <p class="nhs-c-card--summary__name nhs-u-text--ellipsis" data-qa="map-info">
                        ${this.summaryCard.Name}
                    </p>
                    <p class="nhs-c-card__location--summary nhs-u-text--ellipsis" data-qa="map-card-market">
                        ${this.summaryCard.City}, ${this.summaryCard.StateAbbreviation}
                    </p>
                </a>
            </div>
        `;
  }
}
